import { CSSProperties, useRef } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import { Section } from 'ui/molecules/Section/Section';

import styles from './PillarCards.module.scss';

import type { PillarCardsProps } from './PillarCards.types';

const DARK_BACKGROUND = 'linear-gradient(150deg, #050b1a 0%, #081d3d 40%, #0c257f 78%, #042354 100%)';

export const PillarCards = ({
  title,
  description,
  items,
  theme = 'dark',
  columns,
  animated = true,
  glow = true,
  className,
  introBlockClassName,
  bottomContent,
}: PillarCardsProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersection(ref, { threshold: 0.1 });
  const columnCount = columns ?? Math.min(Math.max(items.length, 1), 4);

  return (
    <Section
      theme={theme}
      title={title}
      description={description}
      background={theme === 'dark' ? DARK_BACKGROUND : undefined}
      className={cn(styles[theme], glow && styles.glow, className)}
      introBlockClassName={introBlockClassName}
      bottomContent={bottomContent}
    >
      <div
        ref={ref}
        className={cn(styles.grid, animated && styles.animated, animated && visible && styles.visible)}
        style={{ '--pillar-cols': columnCount } as CSSProperties}
      >
        {items.map((item, idx) => (
          <div key={idx} className={styles.item} style={{ '--pillar-delay': `${idx * 90}ms` } as CSSProperties}>
            {item.eyebrow && (
              <span className={styles.eyebrow}>
                <span className={styles.eyebrowBar} />
                {item.eyebrow}
              </span>
            )}

            <h3 className={styles.title}>{item.title}</h3>

            {item.description && <p className={styles.description}>{item.description}</p>}
          </div>
        ))}
      </div>
    </Section>
  );
};
